import { useEffect, useState } from "react";
import api, { downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Modal } from "@/pages/Settings";
import {
  Plus, Search, Download, AlertTriangle, CheckCircle2, Clock, Trash2, RotateCcw, Bell,
  Building2, Paperclip, Repeat, ChevronRight, ChevronDown, UserCheck,
} from "lucide-react";

const FREQ = ["daily", "weekly", "monthly", "quarterly", "yearly", "once"];
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const STATUS_STYLE = {
  Urgent: { c: "#DC2626", bg: "#FEE2E2", icon: AlertTriangle },
  Pending: { c: "#D97706", bg: "#FEF3C7", icon: Clock },
  Complete: { c: "#16A34A", bg: "#DCFCE7", icon: CheckCircle2 },
};

export default function ProjectTracker() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  const [doneOpen, setDoneOpen] = useState(false);

  const load = () => api.get("/tracker").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/clients?archived=false").then((r) => setClients(r.data));
    api.get("/users").then((r) => setUsers(r.data));
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  const filtered = items.filter((t) => t.client_name.toLowerCase().includes(q.toLowerCase()));
  const active = filtered.filter((t) => t.status !== "Complete");
  const thisMonth = new Date().toISOString().slice(0, 7);
  const done = filtered.filter((t) => t.status === "Complete" && (t.due_date || "").startsWith(thisMonth));
  const urgentDelegated = active.filter((t) => t.status === "Urgent" && t.assigned_to);

  // group active by client
  const groups = {};
  active.forEach((t) => { (groups[t.client_name] = groups[t.client_name] || []).push(t); });
  const rank = { Urgent: 0, Pending: 1 };
  Object.values(groups).forEach((arr) => arr.sort((a, b) => rank[a.status] - rank[b.status]));
  const groupNames = Object.keys(groups).sort();

  const openNew = () => { setForm({ client_id: "", title: "", frequency: "monthly", weekday: 0, due_day: 20, due_month: 1, assigned_to: "", description: "", lead_days: "", start_date: new Date().toISOString().slice(0, 10), attachment_name: "", attachment_type: "", attachment_data: "" }); setOpen(true); };
  const toggleGroup = (name) => setCollapsed((c) => ({ ...c, [name]: !c[name] }));

  const save = async () => {
    try {
      await api.post("/tasks", form);
      toast.success("Task created");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const complete = async (t) => { await api.post("/tracker/complete", { kind: t.kind, ref_id: t.ref_id }); toast.success("Completed & archived"); load(); };
  const reopen = async (t) => { await api.post("/tracker/reopen", { kind: t.kind, ref_id: t.ref_id }); toast.success("Reopened"); load(); };
  const del = async (t) => {
    if (t.kind === "template") { if (!window.confirm("Delete recurring task? (manage schedules in Settings)")) return; await api.delete(`/task-templates/${t.ref_id}`); }
    else { if (!window.confirm("Delete task?")) return; await api.delete(`/tasks/${t.ref_id}`); }
    load();
  };
  const downloadAttachment = async (t) => {
    try { await downloadFile(`/tasks/${t.ref_id}/attachment`, "attachment"); }
    catch (e) { toast.error("No attachment"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[var(--gold)] text-xs uppercase tracking-[0.2em] font-semibold">Feature 3</div>
          <h1 className="font-serif text-4xl mt-1">Project Tracker</h1>
          <p className="text-[var(--text-secondary)] mt-1">Recurring & ad-hoc tasks · grouped by client · auto-updating status</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
            <input data-testid="tracker-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client…" className="pl-9 pr-4 py-2.5 rounded-xl border border-[var(--border-subtle)] bg-white text-sm outline-none focus:border-[var(--gold)] w-52" />
          </div>
          <button data-testid="export-excel-project-tracker-button" onClick={() => downloadFile("/export/tasks", "SV ProjectTracker.xlsx")} className="btn-gold px-4 py-2.5 text-sm flex items-center gap-2"><Download className="w-4 h-4" /> Excel</button>
          <button data-testid="add-task-button" onClick={openNew} className="btn-gold px-4 py-2.5 text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Task</button>
        </div>
      </div>

      {urgentDelegated.length > 0 && (
        <div data-testid="urgent-banner" className="rounded-2xl p-4 flex items-center gap-3 pulse-red" style={{ background: "#DC2626", color: "#fff" }}>
          <Bell className="w-5 h-5" />
          <div className="flex-1">
            <div className="font-semibold text-sm">{urgentDelegated.length} urgent delegated task(s) need attention!</div>
            <div className="text-xs text-white/80">{urgentDelegated.map((t) => `${t.title} → ${t.assigned_name}`).join(" · ")}</div>
          </div>
        </div>
      )}

      {groupNames.map((name) => (
        <div key={name} className="card-elegant overflow-hidden">
          <button onClick={() => toggleGroup(name)} data-testid={`client-group-${name}`} className="w-full flex items-center gap-3 px-5 py-3 bg-[var(--obsidian)] text-white text-left">
            {collapsed[name] ? <ChevronRight className="w-4 h-4 text-[var(--gold)]" /> : <ChevronDown className="w-4 h-4 text-[var(--gold)]" />}
            <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center overflow-hidden">
              {groups[name][0].client_logo ? <img src={groups[name][0].client_logo} className="w-full h-full object-cover" /> : <Building2 className="w-4 h-4 text-[var(--gold)]" />}
            </div>
            <span className="font-serif text-lg flex-1">{name}</span>
            <span className="text-xs text-white/50">{groups[name].length} task(s)</span>
          </button>
          {!collapsed[name] && (
          <table className="w-full text-sm">
            <tbody>
              {groups[name].map((t) => {
                const s = STATUS_STYLE[t.status];
                return (
                  <tr key={t.id} data-testid={`task-row-${t.ref_id}`} className="border-t border-[var(--border-subtle)] hover:bg-[var(--gold-light)]/40">
                    <td className="py-3 px-5 w-32">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ color: s.c, background: s.bg }}>
                        <s.icon className="w-3.5 h-3.5" /> {t.status === "Urgent" ? "In Rush" : t.status}
                      </span>
                    </td>
                    <td className="font-medium">
                      <button onClick={() => setDetail(t)} className="hover:text-[var(--gold)] flex items-center gap-1.5">
                        {t.kind === "template" && <Repeat className="w-3.5 h-3.5 text-[var(--teal)]" title="Recurring" />}
                        {t.title}
                        {t.has_attachment && <Paperclip className="w-3.5 h-3.5 text-[var(--text-muted)]" />}
                      </button>
                    </td>
                    <td>
                      {t.assigned_to
                        ? <span className="inline-flex items-center gap-1 text-xs text-[var(--urgent)] font-semibold"><UserCheck className="w-3.5 h-3.5" /> Due {t.due_date}</span>
                        : <span className="capitalize text-xs px-2 py-0.5 rounded bg-[var(--bg-subtle)]">{t.frequency}</span>}
                    </td>
                    <td className="font-mono text-xs">{t.due_date}</td>
                    <td className="text-xs">{t.assigned_name || "—"}</td>
                    <td className="text-right px-5 whitespace-nowrap">
                      <button data-testid={`complete-task-${t.ref_id}`} onClick={() => complete(t)} className="text-[var(--complete)] hover:bg-green-50 p-1.5 rounded" title="Mark complete"><CheckCircle2 className="w-4 h-4" /></button>
                      {can("delete") && <button onClick={() => del(t)} className="text-[var(--text-muted)] hover:text-[var(--urgent)] p-1.5" title="Delete"><Trash2 className="w-4 h-4" /></button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
      ))}
      {active.length === 0 && <div className="card-elegant py-12 text-center text-[var(--text-muted)]">No active tasks. Add scheduled tasks in Settings or create an ad-hoc task.</div>}

      {done.length > 0 && (
        <div>
          <button onClick={() => setDoneOpen((v) => !v)} data-testid="completed-toggle" className="font-serif text-xl mb-3 flex items-center gap-2 text-[var(--text-secondary)]">
            {doneOpen ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            <CheckCircle2 className="w-5 h-5 text-[var(--complete)]" /> Completed this month ({done.length})
          </button>
          {doneOpen && (
          <div className="card-elegant overflow-hidden">
            <table className="w-full text-sm">
              <tbody>
                {done.map((t) => (
                  <tr key={t.id} className="border-b border-[var(--border-subtle)] last:border-0 opacity-70">
                    <td className="py-3 px-5 font-medium line-through">{t.title}</td>
                    <td className="text-[var(--text-secondary)]">{t.client_name}</td>
                    <td className="capitalize text-xs">{t.frequency}</td>
                    <td className="text-right px-5">
                      <button onClick={() => reopen(t)} className="text-[var(--gold)] text-xs font-semibold flex items-center gap-1 ml-auto"><RotateCcw className="w-3.5 h-3.5" /> Reopen</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}

      {detail && (
        <Modal title={detail.title} onClose={() => setDetail(null)}>
          <div className="space-y-3 text-sm">
            <div className="flex gap-6"><span className="text-[var(--text-muted)] w-24">Client</span><span className="font-medium">{detail.client_name}</span></div>
            <div className="flex gap-6"><span className="text-[var(--text-muted)] w-24">Frequency</span><span className="capitalize">{detail.frequency}</span></div>
            <div className="flex gap-6"><span className="text-[var(--text-muted)] w-24">Due</span><span className="font-mono">{detail.due_date}</span></div>
            <div className="flex gap-6"><span className="text-[var(--text-muted)] w-24">Assigned</span><span>{detail.assigned_name || "—"}</span></div>
            <div>
              <span className="text-[var(--text-muted)]">Description</span>
              <p className="mt-1 bg-[var(--bg-subtle)] rounded-lg p-3 whitespace-pre-wrap">{detail.description || "No description."}</p>
            </div>
            {detail.has_attachment && (
              <button data-testid="download-attachment-button" onClick={() => downloadAttachment(detail)} className="btn-gold px-4 py-2 text-sm flex items-center gap-2"><Paperclip className="w-4 h-4" /> Download Attachment</button>
            )}
          </div>
        </Modal>
      )}

      {open && form && (
        <Modal title="New Ad-hoc Task" onClose={() => setOpen(false)}>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)]">Client</label>
              <select data-testid="task-client-select" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white text-sm outline-none focus:border-[var(--gold)]">
                <option value="">Select client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)]">Task Title</label>
              <input data-testid="task-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none focus:border-[var(--gold)]" placeholder="e.g. Prepare correction SPT" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-semibold text-[var(--text-secondary)]">Frequency</label>
                <select value={form.frequency} onChange={(e) => setForm({ ...form, frequency: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white text-sm capitalize outline-none focus:border-[var(--gold)]">
                  {FREQ.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              {form.frequency === "weekly" ? (
                <div>
                  <label className="text-xs font-semibold text-[var(--text-secondary)]">Weekday</label>
                  <select value={form.weekday} onChange={(e) => setForm({ ...form, weekday: +e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white text-sm outline-none">
                    {WEEKDAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </select>
                </div>
              ) : form.frequency !== "daily" && (
                <div>
                  <label className="text-xs font-semibold text-[var(--text-secondary)]">Due Day</label>
                  <input type="number" min="1" max="31" value={form.due_day} onChange={(e) => setForm({ ...form, due_day: +e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none focus:border-[var(--gold)]" />
                </div>
              )}
              {form.frequency === "yearly" && (
                <div>
                  <label className="text-xs font-semibold text-[var(--text-secondary)]">Due Month</label>
                  <input type="number" min="1" max="12" value={form.due_month} onChange={(e) => setForm({ ...form, due_month: +e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none focus:border-[var(--gold)]" />
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)]">Description / Tax Detail</label>
              <textarea data-testid="task-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows="3" className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none focus:border-[var(--gold)]" placeholder="Explain the detail of the tax task…" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)]">Attachment (import from device)</label>
              <input data-testid="task-attachment-input" type="file" onChange={(e) => {
                const file = e.target.files?.[0]; if (!file) return;
                if (file.size > 3 * 1024 * 1024) { toast.error("File must be under 3MB"); return; }
                const reader = new FileReader();
                reader.onload = () => setForm((f) => ({ ...f, attachment_name: file.name, attachment_type: file.type, attachment_data: reader.result }));
                reader.readAsDataURL(file);
              }} className="mt-1 text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[var(--gold)] file:text-white file:cursor-pointer" />
              {form.attachment_name && <div className="text-xs text-[var(--teal)] mt-1">Attached: {form.attachment_name}</div>}
            </div>
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)]">Active Start Date</label>
              <input data-testid="task-start-date" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1 w-48 px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none focus:border-[var(--gold)]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)]">Reminder lead time (days before due it turns Urgent) — blank = default</label>
              <input data-testid="task-lead-days" type="number" min="0" max="60" value={form.lead_days} onChange={(e) => setForm({ ...form, lead_days: e.target.value === "" ? "" : +e.target.value })} className="mt-1 w-40 px-3 py-2 rounded-lg border border-[var(--border-subtle)] text-sm outline-none focus:border-[var(--gold)]" placeholder="e.g. 5" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[var(--text-secondary)]">Delegate To (optional)</label>
              <select value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-subtle)] bg-white text-sm outline-none focus:border-[var(--gold)]">
                <option value="">— Unassigned —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-[var(--border-subtle)]">Cancel</button>
            <button data-testid="task-save-button" onClick={save} disabled={!form.client_id || !form.title} className="btn-gold px-5 py-2 text-sm disabled:opacity-50">Create Task</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
